export interface BodyBlock {
  type: 'h2' | 'p';
  text: string;
}

export interface TimelineItem {
  time: string;
  text: string;
}

export interface GalleryItem {
  src: string;
  alt: string;
  caption: string;
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  categoryId: string;
  category: string;
  excerpt: string;
  cover: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: number;
  views: number;
  tags: string[];
  featured?: boolean;
  hot?: boolean;
  keyPoints: string[];
  timeline: TimelineItem[];
  gallery: GalleryItem[];
  body: BodyBlock[];
}

export const articles: Article[] = [
  {
    id: 'a01',
    slug: 'variety-show-center-stage-casting-table',
    title: '综艺"C 位之争"幕后线报：一张选角表里的三方博弈',
    categoryId: 'ent',
    category: '娱乐线报',
    excerpt:
      '节目开播前两周，一张标注着三色记号的选角表在制作群里流转。我们从表头、修改记录和沟通时间，还原这场看不见的排位过程。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20stage%20lighting%20and%20ranked%20blocks%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%20layout%2C%20subtle%20grain%20texture%2C%20no%20text%2C%20harmonious%20composition&width=1000&height=640&seq=jiemi-ent01-cover&orientation=landscape',
    author: '沈知白',
    authorRole: '文娱线报组',
    date: '2026-09-08',
    readTime: 7,
    views: 48210,
    tags: ['选角流程', '节目制作', '幕后线报'],
    featured: true,
    hot: true,
    keyPoints: [
      '选角表共经历 11 次修改，其中 7 次集中在开播前 10 天内',
      '三色记号分别代表"平台意向""制作方推荐""艺人方诉求"',
      '最终排位与首播镜头时长的相关性约为 0.68，并非完全对应',
      '超过一半的调整来自档期冲突，而非番位本身',
    ],
    timeline: [
      { time: '开播前 30 天', text: '初版选角表确定，番位按签约顺序排列，尚未划分镜头权重。' },
      { time: '开播前 14 天', text: '平台方介入，提出"首期需要更强记忆点"，表格出现第一批蓝色记号。' },
      { time: '开播前 5 天', text: '两档同期项目出现档期重叠，表格大幅重排，涉及 4 位嘉宾。' },
      { time: '首播后 3 天', text: '镜头分配图流出，观众开始比对排位与实际出镜时长。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=warm%20cream%20paper%20textured%20infographic%20showing%20abstract%20colored%20markers%20and%20grid%20rows%2C%20vermilion%20and%20gold%20flat%20shapes%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean%20composition&width=1200&height=760&seq=jiemi-ent01-gal1&orientation=landscape',
        alt: '选角表三色记号示意图',
        caption: '图解：三色记号并非正式制度，而是三方沟通时形成的约定俗称。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20data%20visualization%20of%20overlapping%20timeline%20bands%20on%20warm%20paper%20background%2C%20muted%20red%20gold%20and%20pine%20green%20accents%2C%20flat%20editorial%20infographic%20style%2C%20no%20text%2C%20balanced%20layout&width=1200&height=760&seq=jiemi-ent01-gal2&orientation=landscape',
        alt: '档期重叠时间带示意图',
        caption: '信息图：两档节目档期重叠的 12 天，是表格重排最密集的区间。',
      },
    ],
    body: [
      { type: 'p', text: '节目官宣阵容那天，评论区最先讨论的不是内容，而是谁站在了海报的中间。番位这件事，在综艺里很少被正式写进合同，却几乎每一季都会成为开播前后最热的话题。' },
      { type: 'h2', text: '一张表，三种记号' },
      { type: 'p', text: '我们拿到的选角表并不复杂：左侧是嘉宾姓名与档期，右侧是备注栏。真正让这张表变得有信息量的，是备注栏里出现的三种颜色记号——蓝色标注平台意向，红色标注制作方推荐，绿色则来自艺人方沟通时的诉求。' },
      { type: 'p', text: '值得注意的是，这三种颜色从未出现在任何正式文件里。它们更像是一种沟通习惯：当三方意见不一致时，用颜色把分歧先标出来，再逐条讨论。' },
      { type: 'h2', text: '调整次数背后是档期，不是番位' },
      { type: 'p', text: '把 11 次修改按时间排列后，一个反直觉的结论出现了：超过一半的调整并非为了争番位，而是因为档期冲突。开播前 5 天那次涉及 4 位嘉宾的大重排，起因是两位嘉宾的另一个项目临时改档。' },
      { type: 'p', text: '换句话说，观众看到的海报排序，很大程度上是档期协调的结果，而不是某一次博弈的胜负。' },
      { type: 'h2', text: '排位与镜头：相关但不相等' },
      { type: 'p', text: '我们把最终排位与首播镜头时长做了简单比对，相关系数约为 0.68。相关，但没有到"排第几就给多少镜头"的程度。首期镜头更多的，是那些能在互动环节制造记忆点的嘉宾。' },
      { type: 'p', text: '这也解释了为什么开播后总会有"镜头分配不均"的讨论——观众看的是排位，剪辑看的是节奏。两者的目标从一开始就不完全一样。' },
      { type: 'p', text: '需要说明的是，本文所依据的表格为沟通阶段的过程稿，不代表最终播出结果。我们把它整理出来，是希望把"排位"这件事从情绪讨论拉回到流程本身。' },
    ],
  },
  {
    id: 'a02',
    slug: 'viral-brand-factory-line-origin',
    title: '网红品牌"爆款"配方溯源：代工厂流水线上的信息差',
    categoryId: 'biz',
    category: '商业内幕',
    excerpt:
      '同一个配方、同一条产线，贴着不同品牌名卖出了三倍价差。我们沿着代工链路，把中间那层信息差逐项拆开。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20factory%20conveyor%20line%20and%20stacked%20product%20blocks%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%20style%2C%20no%20text%2C%20harmonious%20composition&width=1000&height=640&seq=jiemi-biz01-cover&orientation=landscape',
    author: '顾亦南',
    authorRole: '商业内幕组',
    date: '2026-09-06',
    readTime: 8,
    views: 39680,
    tags: ['代工链路', '品牌溢价', '供应链'],
    hot: true,
    keyPoints: [
      '同一条产线的同一配方，出厂成本约为终端售价的 8%–12%',
      '三倍价差主要来自包装、认证与渠道分成，而非内容物',
      '代工厂通常受保密协议约束，无法公开客户名单',
      '信息差的核心是"配方可复制、信任不可复制"',
    ],
    timeline: [
      { time: '第 1 阶段', text: '品牌方提出需求，代工厂提供 3–5 版基础配方供选择。' },
      { time: '第 2 阶段', text: '确定配方后进入包装与备案环节，约占整体周期的四成。' },
      { time: '第 3 阶段', text: '同一配方为不同客户生产时，仅调整香型与包装规格。' },
      { time: '第 4 阶段', text: '终端定价由渠道结构决定，与出厂成本关联度有限。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20cost%20breakdown%20rings%20on%20warm%20cream%20textured%20paper%2C%20vermilion%20red%20gold%20and%20pine%20green%20segments%2C%20editorial%20magazine%20style%2C%20abstract%20shapes%2C%20no%20text&width=1200&height=760&seq=jiemi-biz01-gal1&orientation=landscape',
        alt: '终端售价成本结构示意图',
        caption: '信息图：终端售价中，内容物成本占比通常不到九分之一。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20flow%20diagram%20of%20factory%20to%20shelf%20journey%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20blocks%20connected%20by%20thin%20lines%2C%20flat%20editorial%20infographic%20style%2C%20no%20text&width=1200&height=760&seq=jiemi-biz01-gal2&orientation=landscape',
        alt: '出厂到上架链路示意图',
        caption: '图解：从出厂到上架，中间经过四层加价环节。',
      },
    ],
    body: [
      { type: 'p', text: '一个在直播间卖出高价的"爆款"，和超市货架上平平无奇的同类产品，可能出自同一条产线、同一个配方。这不是秘密，但很少有人把中间的加价环节一项项摊开。' },
      { type: 'h2', text: '配方是怎么被"发明"的' },
      { type: 'p', text: ' contacted 工厂后我们了解到，配方开发并不总是从零开始。品牌方提出需求后，工厂往往会拿出几版成熟的基础配方供选择，再在此基础上调整香型、质地与规格。' },
      { type: 'p', text: '这意味着，所谓"独家配方"有时只是同一基础版本的微调。区别更多体现在命名、包装与讲述方式上。' },
      { type: 'h2', text: '三倍价差钱花在哪' },
      { type: 'p', text: '把链路拆开看：内容物成本约占终端售价的 8%–12%，包装与认证约占两成，渠道分成与营销费用占去剩余的大部分。定价的锚点从来不是成本，而是消费者愿意相信的故事。' },
      { type: 'p', text: '这也解释了为什么同一配方能卖出不同价格——被溢价的部分，本质上是信任的价格。' },
      { type: 'h2', text: '信息差的边界在哪里' },
      { type: 'p', text: '代工厂通常受保密协议约束，无法公开客户名单，这让外界很难直接比对。但一个基本判断依然成立：配方可以复制，稳定交付与品牌信任很难复制。' },
      { type: 'p', text: '对消费者而言，比"同厂不同价"更值得关注的，是产品是否通过了相应类别的备案与检验。这一点是公开可查的。' },
    ],
  },
  {
    id: 'a03',
    slug: 'ai-launch-keynote-vs-delivery',
    title: 'AI 发布会"肌肉秀"拆解：参数背后的真实交付',
    categoryId: 'tech',
    category: '科技真相',
    excerpt:
      '舞台上的跑分、Demo 与"即将开放"之间，隔着一条容易被忽略的时间带。我们把近一年的发布会承诺逐项对照交付结果。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20processor%20chip%20and%20ascending%20bar%20chart%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious%20composition&width=1000&height=640&seq=jiemi-tech01-cover&orientation=landscape',
    author: '陆听澜',
    authorRole: '科技真相组',
    date: '2026-09-04',
    readTime: 6,
    views: 31540,
    tags: ['发布会', '参数对照', '交付节奏'],
    keyPoints: [
      '跑分演示使用的多为特定配置环境，与零售版存在差异',
      '43 项承诺中，按期交付比例约为六成',
      '延迟承诺中，"等待监管与合规"占比最高',
      '观众最容易记住的是 Demo 画面，而非小字说明',
    ],
    timeline: [
      { time: '发布会当天', text: '公布跑分与 Demo，并标注"实验室环境，量产版可能不同"。' },
      { time: '+1 个月', text: '首批预约开放，部分功能标注为"逐步放开"。' },
      { time: '+3 个月', text: '零售版评测解禁，实测与发布会数据的差距开始被讨论。' },
      { time: '+6 个月', text: '大部分承诺功能上线，个别能力以"测试版"形式提供。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20comparison%20chart%20of%20two%20bars%20side%20by%20side%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20gold%20flat%20shapes%2C%20editorial%20magazine%20infographic%20style%2C%20no%20text%2C%20clean%20grid&width=1200&height=760&seq=jiemi-tech01-gal1&orientation=landscape',
        alt: '实验室数据与实测数据对照图',
        caption: '信息图：实验室环境与零售机实测之间的常见差距区间。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20timeline%20of%20six%20milestone%20dots%20connected%20on%20warm%20paper%20background%2C%20muted%20red%20gold%20pine%20green%20accents%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-tech01-gal2&orientation=landscape',
        alt: '承诺交付时间线示意图',
        caption: '图解：从发布会到功能全量上线的典型时间分布。',
      },
    ],
    body: [
      { type: 'p', text: '每一次科技发布会都像一场精心编排的演出：灯光、音效、不断切换的对比图表。问题在于，观众记住的往往是 Demo 的那几秒，而真正决定体验的，是几个月后的交付。' },
      { type: 'h2', text: '跑分为什么总要加小字' },
      { type: 'p', text: '几乎所有发布会数据都会附带一句"实验室环境，量产版可能不同"。这句话不是免责套话，而是真实存在的差异：散热、固件版本、后台负载都会影响结果。' },
      { type: 'p', text: '所以我们做对照时，会把发布日数据与零售版评测数据放在同一张图里，而不是分开展示。差距本身，就是信息。' },
      { type: 'h2', text: '43 项承诺的交付节奏' },
      { type: 'p', text: '我们整理了近一年三场发布会的 43 项功能承诺，按期交付的约为六成。延迟的原因里，"等待合规与监管审批"出现频率最高，其次是算力与供应链排期。' },
      { type: 'p', text: '从时间分布看，发布会后的第三个月是关键窗口：评测解禁、首批退货与功能上线往往集中在这一区间。' },
      { type: 'h2', text: '怎么读一场发布会' },
      { type: 'p', text: '与其记住峰值数字，不如记住三个问题：这个能力是默认开启还是需要申请，是全量开放还是少量灰度，以及它承诺的是"现在可用"还是"即将开放"。' },
      { type: 'p', text: '把这三个问题问清楚，发布会的信息密度会立刻上升一个层级。' },
    ],
  },
  {
    id: 'a04',
    slug: 'top-streamer-team-turnover-ledger',
    title: '头部主播团队离职潮复盘：分成表与对赌条款',
    categoryId: 'fin',
    category: '财经观察',
    excerpt:
      '三个月内，同一个团队走了七个人。把分成表、对赌条款与结算周期放在一起看，离职原因比"内讧"复杂得多。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20ledger%20pages%20and%20percentage%20rings%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-fin01-cover&orientation=landscape',
    author: '周叙白',
    authorRole: '财经观察组',
    date: '2026-09-02',
    readTime: 7,
    views: 28970,
    tags: ['分成结构', '对赌条款', '结算周期'],
    keyPoints: [
      '团队分成通常分为底薪、阶梯佣金与年终对赌三部分',
      '结算周期从 45 天拉长到 90 天后，现金流压力显著上升',
      '对赌目标按季度上调，叠加口径变化后实际难度翻倍',
      '离职集中期与两次结算延迟高度重合',
    ],
    timeline: [
      { time: '第 1 个月', text: '结算周期由 45 天调整为 90 天，团队内部首次出现观望情绪。' },
      { time: '第 2 个月', text: '季度对赌目标上调，同时新增"有效成交"口径，争议集中爆发。' },
      { time: '第 3 个月', text: '首批离职发生，集中在中台与选品岗位。' },
      { time: '第 4 个月', text: '团队启动结构重组，部分职能外包。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20stacked%20commission%20tiers%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20and%20pine%20green%20bars%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean&width=1200&height=760&seq=jiemi-fin01-gal1&orientation=landscape',
        alt: '阶梯佣金结构示意图',
        caption: '信息图：佣金分为三段阶梯，越往上门槛抬升越快。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20cashflow%20curve%20diagram%20on%20warm%20paper%20background%2C%20muted%20red%20and%20gold%20line%20charts%2C%20flat%20editorial%20infographic%20style%2C%20no%20text%2C%20balanced&width=1200&height=760&seq=jiemi-fin01-gal2&orientation=landscape',
        alt: '结算周期变化与现金流示意图',
        caption: '图解：结算周期拉长后，团队月度现金流曲线的变化。',
      },
    ],
    body: [
      { type: 'p', text: '外界把这场离职概括为"团队内讧"，但把时间轴拉出来之后，故事更像是一道算术题：收入分几段、什么时候到账、目标怎么算。' },
      { type: 'h2', text: '分成表的三段结构' },
      { type: 'p', text: '头部团队的分配通常拆成三块：固定底薪、按成交额阶梯递增的佣金，以及年底统一结算的对赌奖金。前两块保底，第三块才是真正拉开差距的部分。' },
      { type: 'p', text: '问题在于，第三块的触发条件写得越复杂，临界的争议就越多。' },
      { type: 'h2', text: '结算周期是压垮骆驼的那根稻草' },
      { type: 'p', text: '当结算周期从 45 天延长到 90 天，意味着同样的工作量要多垫一个月的现金流。对月薪结构中佣金占大头的中台岗位来说，影响是直接的。' },
      { type: 'p', text: '我们统计的离职时间点，与两次结算延迟的间隔高度重合，这不是巧合。' },
      { type: 'h2', text: '口径变化比目标上调更致命' },
      { type: 'p', text: '同比目标上调本身可以理解，但当"成交"的定义被换成"有效成交"——剔除退货、剔除低价单——实际达成难度会翻倍，而员工往往在结算日才意识到这一点。' },
      { type: 'p', text: '透明、可预期，是这类团队留住人的底层条件。一旦规则开始漂移，再高的名义分成也留不住人。' },
    ],
  },
  {
    id: 'a05',
    slug: 'how-a-meme-spreads-across-platforms',
    title: '短视频热梗诞生记：一条线报如何跑遍全网',
    categoryId: 'opi',
    category: '文娱舆情',
    excerpt:
      '一个梗从某个小号发出，到铺满首页，中间经历了什么？我们用三次抓样的数据，画出它的扩散路径。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20network%20nodes%20and%20spreading%20waves%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious%20composition&width=1000&height=640&seq=jiemi-opi01-cover&orientation=landscape',
    author: '苏念',
    authorRole: '文娱舆情组',
    date: '2026-08-31',
    readTime: 6,
    views: 25410,
    tags: ['热梗传播', '分发链路', '舆情观测'],
    keyPoints: [
      '热梗扩散通常经历"小号试水—腰部搬运—头部定型"三阶段',
      '从试水到顶流，中位数约为 33 小时',
      '评论区二次创作对传播的贡献常常高于原帖',
      '同一梗在不同平台会经历语义漂移',
    ],
    timeline: [
      { time: '第 0 小时', text: '小号发布原始素材，互动量低但停留时长异常高。' },
      { time: '第 9 小时', text: '腰部账号开始搬运并加字幕，出现第一个二次创作版本。' },
      { time: '第 21 小时', text: '同一梗出现变体解读，语义开始偏离原始语境。' },
      { time: '第 33 小时', text: '头部账号定型传播，词条进入搜索联想。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20diffusion%20curve%20with%20three%20phases%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20pine%20green%20markers%2C%20flat%20editorial%20infographic%20style%2C%20no%20text%2C%20clean%20grid&width=1200&height=760&seq=jiemi-opi01-gal1&orientation=landscape',
        alt: '热梗扩散三阶段曲线图',
        caption: '信息图：扩散曲线的三个阶段，腰部搬运是斜率最陡的一段。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20network%20graph%20of%20connected%20dots%20growing%20outward%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20nodes%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-opi01-gal2&orientation=landscape',
        alt: '跨平台传播链路示意图',
        caption: '图解：同一素材跨平台传播时的节点扩散结构。',
      },
    ],
    body: [
      { type: 'p', text: '热梗很少是"突然"火的。在铺满首页之前，它通常先在一个不起眼的角落安静地待了几个小时。' },
      { type: 'h2', text: '第一阶段：小号试水' },
      { type: 'p', text: '原始素材往往由小体量账号发出，互动量平平，但有一个指标异常突出——停留时长。愿意看完的人比点赞的人多，这是内容有潜力的早期信号。' },
      { type: 'p', text: '这段时间内容几乎不产生传播，却决定了它会不会被搬运者注意到。' },
      { type: 'h2', text: '第二阶段：腰部搬运拉出斜率' },
      { type: 'p', text: '真正的爆发点来自腰部账号。它们不创造梗，但擅长把长素材剪短、加字幕、换 BGM。扩散曲线最陡的一段，几乎都出现在这个阶段。' },
      { type: 'p', text: '评论区的作用常被低估。很多变体解读正是在评论区诞生，然后反向成为新的内容素材。' },
      { type: 'h2', text: '第三阶段：语义漂移' },
      { type: 'p', text: '当梗进入头部账号后，它的含义往往已经和原始语境不太一样了。这也是为什么同一个小片段，不同人看到的是完全不同的意思。' },
      { type: 'p', text: '理解传播链路的价值，不是判断哪个版本"正确"，而是知道每个版本分别在哪里被放大了。' },
    ],
  },
  {
    id: 'a06',
    slug: 'community-group-buying-subsidy-retreat',
    title: '社区团购补贴退坡调查：团长流失的三个月',
    categoryId: 'soc',
    category: '社会纵深',
    excerpt:
      '补贴从每单 1.8 元降到 0.6 元，用了不到一个季度。我们走访了 6 个社区的自提点，记录团长们算的那笔账。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20community%20buildings%20and%20parcel%20boxes%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-soc01-cover&orientation=landscape',
    author: '程越',
    authorRole: '社会纵深组',
    date: '2026-08-28',
    readTime: 8,
    views: 22130,
    tags: ['社区团购', '补贴退坡', '团长生态'],
    keyPoints: [
      '单均补贴从 1.8 元降至 0.6 元，降幅达 66%',
      '多数团长的盈亏平衡点是日均 60 单',
      '流失高峰出现在补贴下调后的第 3–6 周',
      '留存下来的团长普遍叠加了其他副业收入',
    ],
    timeline: [
      { time: '第 1 个月', text: '平台调整补贴规则，单均激励下调，团长社群出现讨论。' },
      { time: '第 2 个月', text: '部分自提点关闭，用户被合并到邻近点位。' },
      { time: '第 3 个月', text: '活跃团长数量趋于稳定，留存者开始转向组合经营。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20break%20even%20threshold%20chart%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20pine%20green%20accents%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean&width=1200&height=760&seq=jiemi-soc01-gal1&orientation=landscape',
        alt: '团长盈亏平衡点示意图',
        caption: '信息图：补贴调整前后的盈亏平衡单量对比。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20declining%20line%20chart%20with%20three%20stages%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20markers%2C%20flat%20editorial%20infographic%20style%2C%20no%20text&width=1200&height=760&seq=jiemi-soc01-gal2&orientation=landscape',
        alt: '团长活跃数量变化曲线',
        caption: '图解：补贴下调后团长活跃数量的三个月变化。',
      },
    ],
    body: [
      { type: 'p', text: '一个自提点就是一本小账本。场地、时间、人情、损耗，算清楚了才敢继续做下去。补贴一降，这本账就要重算。' },
      { type: 'h2', text: '从 1.8 元到 0.6 元' },
      { type: 'p', text: '补贴下调不是一步到位的，而是分两次完成。第一次下调后，很多团长尚能靠单量增长弥补；第二次下调叠加获客奖励取消，账就算不平了。' },
      { type: 'p', text: '多数团长的盈亏平衡点在日均 60 单上下。低于这条线，付出的时间就不再划算。' },
      { type: 'h2', text: '流失不是瞬间发生的' },
      { type: 'p', text: '走访中我们发现，团长退出普遍滞后于政策调整三到六周。原因很现实：先观望，再减少投入，最后才正式交接。' },
      { type: 'p', text: '这也让平台方的数据呈现出一种"延迟回弹"的错觉——看起来还在维持，实际已经在流失。' },
      { type: 'h2', text: '留下来的人在做什么' },
      { type: 'p', text: '没有退出的团长，几乎都不是只靠这一项收入。他们把自提点和社区小店、快递代收、团购分销叠在一起，用同一份场地和时间覆盖多项收益。' },
      { type: 'p', text: '补贴退坡没有终结社区团购，但它筛选出了一种更朴素的经营方式：把每一项服务都算进同一本账。' },
    ],
  },
  {
    id: 'a07',
    slug: 'series-filler-episode-accounting',
    title: '剧集"注水"工业化操作：一集拆成两集的账本',
    categoryId: 'ent',
    category: '娱乐线报',
    excerpt:
      '同样的素材，剪辑台上可以产出 24 集，也可以产出 36 集。我们对照了播出母带与制作阶段的分集脚本，看差额从哪里来。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20film%20strip%20and%20split%20timeline%20blocks%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-ent02-cover&orientation=landscape',
    author: '沈知白',
    authorRole: '文娱线报组',
    date: '2026-08-26',
    readTime: 7,
    views: 20880,
    tags: ['剧集制作', '剪辑结构', '分集逻辑'],
    keyPoints: [
      '播出母带与制作脚本的分集数量相差 12 集',
      '注水主要来自回顾片段、慢镜头与重复转场',
      '单集有效剧情时长占比可低至 61%',
      '分集数量往往在后期商务谈判阶段被重新确定',
    ],
    timeline: [
      { time: '制作期', text: '按 24 集完成拍摄与初剪，叙事节奏相对紧凑。' },
      { time: '后期中期', text: '收到平台反馈，建议"拉长集数以匹配排播窗口"。' },
      { time: '后期末期', text: '通过拆集、插入回顾片段完成扩集，母带增至 36 集。' },
      { time: '播出期', text: '观众对节奏问题集中反馈，弹幕出现"倍速观看"提示。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20episode%20length%20distribution%20bars%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20and%20pine%20green%20accents%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean%20grid&width=1200&height=760&seq=jiemi-ent02-gal1&orientation=landscape',
        alt: '单集有效时长分布图',
        caption: '信息图：扩集后单集有效剧情时长的占比变化。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20split%20timeline%20with%20dashed%20cut%20lines%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20blocks%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-ent02-gal2&orientation=landscape',
        alt: '分集拆分位置示意图',
        caption: '图解：扩集时最常使用的四种拆分方式。',
      },
    ],
    body: [
      { type: 'p', text: '"注水"这个词听起来像是创作者偷懒，但把流程摆开之后会发现，它更多是一个关于集数与排播的结构问题。' },
      { type: 'h2', text: '24 集与 36 集之间' },
      { type: 'p', text: '同一批素材，按叙事逻辑剪是 24 集，按排播需求剪可以是 36 集。差额不是新增内容，而是把原有的镜头重新切分、把转场拉长、增加回顾片段。' },
      { type: 'p', text: '在一些单集里，真正推进剧情的时长只占六成左右，其余是情绪镜头与重复信息。' },
      { type: 'h2', text: '为什么要拉长' },
      { type: 'p', text: '集数直接影响排播周期、广告位与版权计价方式。在部分合作模式下，集数越多，可售卖的时段就越多，这让"扩集"成了一种理性选择。' },
      { type: 'p', text: '问题不在于选择本身，而在于选择发生在后期而非创作期，演员与编剧往往没有参与。' },
      { type: 'h2', text: '观众的应对方式' },
      { type: 'p', text: '倍速播放、只看关键情节、按集数跳看——观众用观看习惯给出了反馈。当一集的"信息密度"下降，观看行为自然会调整。' },
      { type: 'p', text: '对创作者来说，真正值得警惕的不是集数变多，而是观众开始不相信这一集会有新信息。' },
    ],
  },
  {
    id: 'a08',
    slug: 'tea-brand-collab-authorized-goods',
    title: '新茶饮联名狂热背后：授权函与限量款的真相',
    categoryId: 'biz',
    category: '商业内幕',
    excerpt:
      '"限量"两个字的背后，是授权周期、生产批次和门店配额共同决定的。我们拆开一份联名合作的流程表。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20cup%20cups%20and%20license%20stamp%20shapes%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-biz02-cover&orientation=landscape',
    author: '顾亦南',
    authorRole: '商业内幕组',
    date: '2026-08-24',
    readTime: 6,
    views: 19460,
    tags: ['品牌联名', '授权周期', '限量策略'],
    keyPoints: [
      '一次联名的完整授权周期通常为 6–10 个月',
      '"限量"多指生产批次而非绝对数量',
      '周边配额按门店等级分配，热门门店可拿到 3 倍份额',
      '联名款的生命周期普遍在 4–8 周',
    ],
    timeline: [
      { time: 'T-8 个月', text: '品牌双方接触，确定联名主题与授权范围。' },
      { time: 'T-4 个月', text: '周边设计与打样完成，确定首批生产批次。' },
      { time: 'T-0', text: '联名上线，周边按门店等级分配，部分门店首日售罄。' },
      { time: 'T+6 周', text: '热度回落，剩余周边转为常规赠品或库存消化。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20eight%20month%20project%20timeline%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20and%20pine%20green%20markers%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean&width=1200&height=760&seq=jiemi-biz02-gal1&orientation=landscape',
        alt: '联名授权周期时间线',
        caption: '信息图：一次联名从接触到上线的八个月周期。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20allocation%20blocks%20distributed%20across%20tiered%20bins%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20shapes%2C%20flat%20editorial%20infographic%20style%2C%20no%20text&width=1200&height=760&seq=jiemi-biz02-gal2&orientation=landscape',
        alt: '门店周边配额分配示意图',
        caption: '图解：周边配额按门店等级分配的常见比例结构。',
      },
    ],
    body: [
      { type: 'p', text: '排队三小时买到一只联名杯套，第二天却发现隔壁门店还有一堆库存——这种落差感，几乎每个联名都出现过。它和稀缺的真实程度有关，也和分配方式有关。' },
      { type: 'h2', text: '授权要走八个月' },
      { type: 'p', text: '一次正规联名，从双方接触到正式上线通常需要六到十个月：主题确定、授权范围界定、设计打样、生产排期，每一步都很难压缩。' },
      { type: 'p', text: '这也解释了为什么同一 IP 会在相同时间段出现在多个品牌上——授权是提前很久排好的。' },
      { type: 'h2', text: '"限量"限的是什么' },
      { type: 'p', text: '在很多合作里，"限量"指的是生产批次有限，而不是绝对数量固定。当首批售罄，品牌可以选择加单，也可以选择维持稀缺。' },
      { type: 'p', text: '周边在门店之间的分配同样遵循等级：核心商圈门店拿到的份额，可能是普通门店的两到三倍。' },
      { type: 'h2', text: '一个月后，热度去哪了' },
      { type: 'p', text: '联名款的生命周期普遍在四到八周。热度过去后，剩余周边通常转为常规赠品，或者进入库存消化渠道。' },
      { type: 'p', text: '与其追逐第一天的队伍，不如关注产品本身是否值得复购——这才是品牌真正希望被记住的部分。' },
    ],
  },
  {
    id: 'a09',
    slug: 'smart-hardware-lab-data-teardown',
    title: '智能硬件"实验室数据"里的水分：一份拆机报告图解',
    categoryId: 'tech',
    category: '科技真相',
    excerpt:
      '续航标称与实际差多少，往往取决于测试条件。我们把三款同价位产品的拆机结果与标称值画在同一张图上。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20opened%20device%20and%20battery%20cells%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-tech02-cover&orientation=landscape',
    author: '陆听澜',
    authorRole: '科技真相组',
    date: '2026-08-21',
    readTime: 7,
    views: 17820,
    tags: ['拆机', '续航测试', '参数核实'],
    keyPoints: [
      '三款产品的实测续航均低于标称值，差距在 18%–34% 之间',
      '标称续航多在低负载、恒温环境下测得',
      '电池标称容量与实际可释放容量存在约 5% 差异',
      '散热结构对高负载续航的影响大于电池容量本身',
    ],
    timeline: [
      { time: '第 1 步', text: '常温常亮环境下进行标准续航测试，记录基线数据。' },
      { time: '第 2 步', text: '加入高负载场景，观察散热对续航的影响。' },
      { time: '第 3 步', text: '拆机核对电池标称容量与电芯实际参数。' },
      { time: '第 4 步', text: '将三组数据与官方标称值合并成对照图。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20comparing%20labelled%20versus%20measured%20battery%20bars%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20pine%20green%20accents%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean%20grid&width=1200&height=760&seq=jiemi-tech02-gal1&orientation=landscape',
        alt: '标称续航与实测续航对照图',
        caption: '信息图：三款产品的标称续航与实际测试结果对照。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20thermal%20heatmap%20style%20diagram%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20gradients%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-tech02-gal2&orientation=landscape',
        alt: '高负载散热影响示意图',
        caption: '图解：高负载场景下散热结构对续航的影响路径。',
      },
    ],
    body: [
      { type: 'p', text: '标称续航是发布会 PPT 上最醒目的一行字，也是用户拿到手后最常感到失落的数字。差距从哪来？答案通常不在电池，而在测试条件。' },
      { type: 'h2', text: '测试条件决定了数字上限' },
      { type: 'p', text: '多数标称续航在低负载、恒温、关闭部分传感器的条件下测得。一旦进入日常使用场景——多任务、亮屏、网络波动——实际时长自然下滑。' },
      { type: 'p', text: '我们实测的三款产品，与标称值的差距落在 18% 到 34% 之间。差距本身不算异常，异常的是这个区间很少被公开说明。' },
      { type: 'h2', text: '散热比电池更影响体验' },
      { type: 'p', text: '拆机后发现，三款产品的电池容量差异并不大。真正的体验分水岭在散热结构：热量堆积会触发降频，进而拉高功耗，形成续航的二次损耗。' },
      { type: 'p', text: '换句话说，同样一块电池，散热做得好的设备能用得更久。' },
      { type: 'h2', text: '怎么看待标称值' },
      { type: 'p', text: '标称值不是谎言，但它是一个上限，不是平均值。购买时把标称数字打个七折来预期，通常更接近真实体验。' },
      { type: 'p', text: '这也是我们做拆机与对照图的原因：把条件写清楚，让数字回到它应有的位置上。' },
    ],
  },
  {
    id: 'a10',
    slug: 'concert-ticket-soldout-return-flow',
    title: '演唱会票务"秒空"解密：回流票到底去哪了',
    categoryId: 'fin',
    category: '财经观察',
    excerpt:
      '开票三分钟售罄，两小时后却不断有票放出。我们把开票、回流与二次销售的三个时间窗叠在一起看。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20ticket%20stubs%20and%20seat%20grid%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-fin02-cover&orientation=landscape',
    author: '周叙白',
    authorRole: '财经观察组',
    date: '2026-08-18',
    readTime: 6,
    views: 16350,
    tags: ['票务规则', '回流票', '资金流'],
    keyPoints: [
      '秒空的库存包含未付款订单占用，并非全部已售',
      '未支付订单通常在 15–30 分钟内释放',
      '退票手续费阶梯是回流票出现的第二个窗口',
      '二次销售价格与开票时间存在明显反比关系',
    ],
    timeline: [
      { time: '开票瞬间', text: '通道开放，订单被大量占用，页面显示"已售罄"。' },
      { time: '+15 分钟', text: '首批未支付订单释放，回流票出现第一波。' },
      { time: '+2 小时', text: '阶梯退票手续费进入低位区间，出现第二波回流。' },
      { time: '演出前一周', text: '临近退票截止日，回流票数量达到峰值。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20return%20ticket%20waves%20as%20three%20peaks%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20pine%20green%20accents%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean%20grid&width=1200&height=760&seq=jiemi-fin02-gal1&orientation=landscape',
        alt: '回流票三波释放示意图',
        caption: '信息图：回流票出现的三个主要时间窗口。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20price%20curve%20descending%20over%20time%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20line%20chart%2C%20flat%20editorial%20infographic%20style%2C%20no%20text&width=1200&height=760&seq=jiemi-fin02-gal2&orientation=landscape',
        alt: '二次销售价格变化曲线',
        caption: '图解：临近演出，二次销售溢价逐步收窄。',
      },
    ],
    body: [
      { type: 'p', text: '"三分钟售罄"和"两小时后还有票"，看似矛盾，其实是同一套规则在不同时间窗里的表现。' },
      { type: 'h2', text: '秒空的库存不等于已售出' },
      { type: 'p', text: '开票时大量订单被同时占用，只要未在限定时间内完成支付，库存就会释放。页面上的"已售罄"，描述的是可售库存为零，而不是所有人都买到了票。' },
      { type: 'p', text: '这就是第一波回流票的来源，通常在开票后 15 到 30 分钟内出现。' },
      { type: 'h2', text: '退票阶梯制造第二个窗口' },
      { type: 'p', text: '多数票务平台采用阶梯退票手续费：越早退，手续费越低。于是当手续费进入低位区间时，会出现一波集中退票。' },
      { type: 'p', text: '临近退票截止日前一周，往往是回流票数量最多的时候。' },
      { type: 'h2', text: '耐心比手速更值钱' },
      { type: 'p', text: '从价格曲线看，二次销售溢价随着演出临近而收窄。这意味着，对非刚需的观众来说，等一等往往比抢第一分钟更划算。' },
      { type: 'p', text: '当然，前提是买到正规渠道的回流票——这一点没有捷径。' },
    ],
  },
  {
    id: 'a11',
    slug: 'night-market-queue-economics',
    title: '城市夜市"网红店"排队经济学：一小时队伍的构成',
    categoryId: 'soc',
    category: '社会纵深',
    excerpt:
      '我们在一家夜市的网红摊位前蹲了三个晚上，数了队伍里有多少人真的会下单，又为什么会停下。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20night%20market%20stalls%20with%20warm%20string%20lights%20on%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-soc02-cover&orientation=landscape',
    author: '程越',
    authorRole: '社会纵深组',
    date: '2026-08-15',
    readTime: 6,
    views: 14980,
    tags: ['夜市经济', '排队现象', '摊主经营'],
    keyPoints: [
      '队伍中约 22% 的人最终没有下单',
      '排队时长与摊位出餐节奏强相关，而非人数',
      '同行结伴的人下单概率比单独排队高约 1.5 倍',
      '队伍长度本身会吸引新的排队者',
    ],
    timeline: [
      { time: '18:30', text: '摊位开档，队伍迅速形成，前 20 位多为熟客。' },
      { time: '20:00', text: '人流高峰，队伍长度达到峰值，等待时间约 35 分钟。' },
      { time: '21:30', text: '新客比例上升，弃队率明显提高。' },
      { time: '23:00', text: '收档前最后一波，队伍快速消化。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20queue%20composition%20pie%20segments%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20pine%20green%20accents%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean&width=1200&height=760&seq=jiemi-soc02-gal1&orientation=landscape',
        alt: '队伍人员构成示意图',
        caption: '信息图：队伍里真正下单、观望与随行者的比例构成。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20queue%20length%20versus%20waiting%20time%20chart%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20curves%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-soc02-gal2&orientation=landscape',
        alt: '队伍长度与等待时间关系图',
        caption: '图解：等待时间由出餐节奏决定，并非单纯的人数。',
      },
    ],
    body: [
      { type: 'p', text: '夜市里最显眼的不是食物，是队伍。队伍本身就成了一种招牌，会不断把人吸引进来。但队伍里有多少人真的会买单？' },
      { type: 'h2', text: '队伍是一半生意，一半表演' },
      { type: 'p', text: '蹲守三个晚上后，我们数出一个数字：约 22% 的排队者最终没有下单。他们排队的原因各不相同——陪朋友、拍视频、单纯想看看到底多好吃。' },
      { type: 'p', text: '但他们的存在，让队伍看起来更长，也更有说服力。' },
      { type: 'h2', text: '等待时间由出餐节奏决定' },
      { type: 'p', text: '把队伍长度和实际等待时间对照后，我们发现真正影响等待的是摊位出餐节奏，而不是排队人数。节奏稳定的摊位，即使队伍更长，体验也更好。' },
      { type: 'p', text: '这也是老摊主最在意的一个指标：能不能稳定出餐。' },
      { type: 'h2', text: '结伴排队的人更容易留下' },
      { type: 'p', text: '统计显示，结伴排队的人下单概率比单独排队的约高 1.5 倍。等待被分摊成了聊天的时间，心理成本就下降了。' },
      { type: 'p', text: '对摊主来说，这意味着一件事：把等待变得不难熬，比把队伍变短更有价值。' },
    ],
  },
  {
    id: 'a12',
    slug: 'trailer-marketing-account-matrix',
    title: '影视营销号矩阵运作图：一条预告的转发链路',
    categoryId: 'opi',
    category: '文娱舆情',
    excerpt:
      '同一条预告，在不同账号下的措辞略有差异。这些差异不是巧合，而是分发链路里被设计好的层次。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20magazine%20illustration%20of%20abstract%20play%20buttons%20and%20relay%20arrows%20on%20warm%20cream%20paper%2C%20vermilion%20red%20and%20vintage%20gold%20accents%2C%20flat%20geometric%20infographic%2C%20no%20text%2C%20harmonious&width=1000&height=640&seq=jiemi-opi02-cover&orientation=landscape',
    author: '苏念',
    authorRole: '文娱舆情组',
    date: '2026-08-12',
    readTime: 6,
    views: 13420,
    tags: ['内容分发', '账号矩阵', '舆情链路'],
    keyPoints: [
      '一次预告分发通常覆盖 30–60 个账号，分为四个层级',
      '不同层级使用不同措辞模板，避免内容完全一致',
      '发布时间按分钟错开，形成"自然扩散"观感',
      '评论区管理是链路中成本最高的一环',
    ],
    timeline: [
      { time: 'T-30 分', text: '核心账号率先发布，配合少量预热话题词。' },
      { time: 'T-15 分', text: '第二层账号跟进，措辞调整为更具讨论性的版本。' },
      { time: 'T+0', text: '预告正式上线，第三层账号集中转发，评论区同步运营。' },
      { time: 'T+60 分', text: '第四层长尾账号承接，话题词进入热搜讨论区间。' },
    ],
    gallery: [
      {
        src: 'https://readdy.ai/api/search-image?query=flat%20infographic%20of%20four%20tiered%20account%20layers%20on%20warm%20cream%20paper%2C%20vermilion%20red%20gold%20and%20pine%20green%20blocks%2C%20editorial%20magazine%20style%2C%20no%20text%2C%20clean&width=1200&height=760&seq=jiemi-opi02-gal1&orientation=landscape',
        alt: '账号矩阵四层结构图',
        caption: '信息图：一条预告分发所覆盖的四层账号结构。',
      },
      {
        src: 'https://readdy.ai/api/search-image?query=abstract%20timing%20schedule%20diagram%20with%20staggered%20dots%20on%20warm%20paper%2C%20muted%20red%20and%20gold%20markers%2C%20flat%20editorial%20infographic%2C%20no%20text&width=1200&height=760&seq=jiemi-opi02-gal2&orientation=landscape',
        alt: '分时发布节奏示意图',
        caption: '图解：不同账号的发布时间被刻意错开，形成扩散节奏。',
      },
    ],
    body: [
      { type: 'p', text: '一条预告上线后，你可能会在十几个账号下看到它。措辞各有不同，语气有轻有重。这些差别通常不是每个人自己的发挥。' },
      { type: 'h2', text: '四层账号，四种措辞' },
      { type: 'p', text: '一次完整的预告分发往往覆盖 30 到 60 个账号，并分为四个层级：核心账号定调，第二层制造讨论，第三层集中转发，第四层长尾承接。' },
      { type: 'p', text: '不同层级使用不同的措辞模板，目的很简单——避免内容完全一致而被识别为搬运。' },
      { type: 'h2', text: '错峰发布制造"自然感"' },
      { type: 'p', text: '发布时间被按分钟错开。如果所有账号在同一秒转发，观感反而失真；错峰之后，内容看起来像是自己慢慢扩散开的。' },
      { type: 'p', text: '这是链路里最容易被忽略、也最费心思的一环。' },
      { type: 'h2', text: '真正难的是评论区' },
      { type: 'p', text: '相比发布，评论区运营才是成本最高的部分。引导讨论、回应质疑、控制负面表达，都需要人工介入。' },
      { type: 'p', text: '理解这条链路的意义，不在于判定内容好坏，而在于知道自己在哪一层看到了它。' },
    ],
  },
];

export const featuredArticle: Article = articles[0];

export const hotArticles: Article[] = [...articles].sort((a, b) => b.views - a.views).slice(0, 6);